import { useState, useCallback, useEffect } from 'react'
import type { SunoPromptInput, SunoPromptResult, SavedSunoPrompt } from '@creanizer/types'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useSunoPromptStore } from '../../../../stores/sunoPromptStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useLyricStore } from '../../../../stores/lyricStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { Card } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { toast } from 'sonner'
import { SunoPromptForm } from './SunoPromptForm'
import { SunoPromptResult as ResultDisplay } from './SunoPromptResult'
import { SavedSunoPromptList } from './SavedSunoPromptList'
import { SunoPromptBatchResults } from './SunoPromptBatchResults'
import { SunoErrorBoundary } from './ErrorBoundary'
import { SunoLyricsRegenerateDialog } from './SunoLyricsRegenerateDialog'

// 歌詞からセクション構造をパース
function parseLyricSections(fullLyrics: string): Array<{
  section: string
  content: string
  duration?: string
  instructions?: string
}> {
  const sections: Array<{
    section: string
    content: string
    duration?: string
    instructions?: string
  }> = []
  const lines = fullLyrics.split('\n')
  let currentSection = ''
  let currentContent: string[] = []

  for (const line of lines) {
    const match = line.match(/^\[(.*?)\]/)
    if (match) {
      if (currentSection && currentContent.length > 0) {
        sections.push({
          section: currentSection,
          content: currentContent.join('\n').trim(),
        })
      }
      currentSection = match[1]
      currentContent = []
    } else if (currentSection) {
      if (line.trim()) {
        currentContent.push(line)
      }
    }
  }

  if (currentSection && currentContent.length > 0) {
    sections.push({
      section: currentSection,
      content: currentContent.join('\n').trim(),
    })
  }

  return sections
}

export function SunoPromptGenerator() {
  const generator = useGenerator('suno')
  const { savedPrompts, savePrompt, deletePrompt, updatePrompt } = useSunoPromptStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { lyrics } = useLyricStore()
  const { savedSocieties } = useSocietyStore()

  const [input, setInput] = useState<SunoPromptInput>({
    mode: 'instrumental',
    promptLength: 'medium',
    creativityLevel: 7,
    technicalDetail: 7,
    musicStyle: [],
  })

  const [result, setResult] = useState<SunoPromptResult | null>(null)
  const [batchResults, setBatchResults] = useState<SunoPromptResult[]>([])
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedPrompt, setSelectedPrompt] = useState<SavedSunoPrompt | null>(null)
  const [showBatchSettings, setShowBatchSettings] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false)
  const [targetLyricsSection, setTargetLyricsSection] = useState<string>('')
  const [regenerateError, setRegenerateError] = useState<string | null>(null)
  const [originalPromptBeforeRegenerate, setOriginalPromptBeforeRegenerate] = useState<SavedSunoPrompt | null>(null)
  const [isRegeneratePreview, setIsRegeneratePreview] = useState(false)

  // 生成処理
  const handleGenerate = useCallback(async () => {
    try {
      if (!input.mode) {
        toast.error('モードを選択してください')
        return
      }

      // エラー状態をクリア
      setGenerationError(null)

      const apiResponse = await generator.mutateAsync(input)

      // APIレスポンスの検証
      if (!apiResponse || !apiResponse.result) {
        const errorMsg = 'レスポンスが空です。再度お試しください。'
        setGenerationError(errorMsg)
        toast.error(errorMsg)
        return
      }

      // resultフィールドから実データを取得
      const response = apiResponse.result

      // エラーレスポンスのチェック
      if (typeof response === 'object' && 'error' in response) {
        const errorResponse = response as any
        const errorMsg = `生成エラー: ${errorResponse.error || errorResponse.message || '不明なエラー'}`
        setGenerationError(errorMsg)
        toast.error(errorMsg)
        return
      }

      // バッチモードの処理
      if (input.batchMode && Array.isArray(response)) {
        // 結果の検証
        const validResults = (response as unknown as SunoPromptResult[]).filter(
          result => result && result.promptInfo && result.mainPrompt
        )

        if (validResults.length === 0) {
          const errorMsg = '有効なプロンプトが生成されませんでした。再度お試しください。'
          setGenerationError(errorMsg)
          toast.error(errorMsg)
          return
        }

        setBatchResults(validResults)
        setResult(null)
        toast.success(`${validResults.length}個のプロンプトを生成しました`)
      } else if (!Array.isArray(response)) {
        // 単体結果の検証
        const singleResult = response as unknown as SunoPromptResult

        if (!singleResult.promptInfo || !singleResult.mainPrompt) {
          const errorMsg = 'プロンプトの生成に失敗しました。データが不完全です。'
          setGenerationError(errorMsg)
          toast.error(errorMsg)
          console.error('Invalid result structure:', singleResult)
          return
        }

        setResult(singleResult)
        setBatchResults([])
        toast.success('プロンプトを生成しました')
      } else {
        const errorMsg = '予期しないレスポンス形式です'
        setGenerationError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (error) {
      console.error('Generation error:', error)

      // エラーメッセージの詳細化
      let errorMsg = '生成に失敗しました。再度お試しください。'

      if (error instanceof Error) {
        if (error.message.includes('JSONパースエラー')) {
          errorMsg = 'プロンプトの解析に失敗しました。再度お試しください。'
        } else if (error.message.includes('API_KEY')) {
          errorMsg = 'APIキーが無効です。設定を確認してください。'
        } else if (error.message.includes('レート制限')) {
          errorMsg = error.message
        } else {
          errorMsg = `生成エラー: ${error.message}`
        }
      }

      setGenerationError(errorMsg)
      toast.error(errorMsg)
    }
  }, [input, generator])

  // 保存処理
  const handleSave = useCallback(async (promptResult?: SunoPromptResult, index?: number) => {
    const targetResult = promptResult || result
    if (!targetResult) {
      toast.error('保存するプロンプトがありません')
      return
    }

    try {
      const savedPrompt: SavedSunoPrompt = {
        id: `suno_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        title: targetResult.promptInfo.title || 'Untitled Prompt',
        input,
        result: targetResult,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: [],
        favorite: false,
      }

      await savePrompt(savedPrompt)
      setSelectedPrompt(savedPrompt)
      toast.success('プロンプトを保存しました')
    } catch (error) {
      console.error('Save error:', error)
      toast.error('保存に失敗しました')
    }
  }, [result, input, savePrompt])

  // 一括保存処理
  const handleSaveAll = useCallback(async () => {
    if (batchResults.length === 0) return

    try {
      for (let i = 0; i < batchResults.length; i++) {
        const targetResult = batchResults[i]
        const savedPrompt: SavedSunoPrompt = {
          id: `suno_${Date.now() + i}_${Math.random().toString(36).slice(2, 11)}`,
          title: targetResult.promptInfo.title || `Untitled Prompt ${i + 1}`,
          input,
          result: targetResult,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tags: [],
          favorite: false,
        }
        await savePrompt(savedPrompt)
        // レート制限対策
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      toast.success(`${batchResults.length}個のプロンプトを保存しました`)
    } catch (error) {
      console.error('Save all error:', error)
      toast.error('一括保存に失敗しました')
    }
  }, [batchResults, input, savePrompt])

  // 保存済みチェック
  const isSaved = useCallback((promptResult: SunoPromptResult) => {
    if (!promptResult || !promptResult.promptInfo) return false
    return savedPrompts.some(saved =>
      saved?.result?.promptInfo?.title === promptResult.promptInfo.title &&
      saved?.result?.mainPrompt?.fullPrompt === promptResult.mainPrompt?.fullPrompt
    )
  }, [savedPrompts])

  // 保存済みプロンプトの読み込み
  const handleLoadPrompt = useCallback((prompt: SavedSunoPrompt) => {
    // マイグレーション: 古いデータでmusicStyleが文字列の場合、配列に変換
    const migratedInput = {
      ...prompt.input,
      musicStyle: Array.isArray(prompt.input.musicStyle)
        ? prompt.input.musicStyle
        : prompt.input.musicStyle
          ? [prompt.input.musicStyle as any]
          : []
    }

    setInput(migratedInput)
    setResult(prompt.result)
    setSelectedPrompt(prompt)
    setActiveTab('generate')
    toast.success('プロンプトを読み込みました')
  }, [])

  // 削除処理
  const handleDelete = useCallback(async (id: string) => {
    try {
      await deletePrompt(id)
      toast.success('プロンプトを削除しました')
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('削除に失敗しました')
    }
  }, [deletePrompt])

  // お気に入り切り替え
  const handleToggleFavorite = useCallback(async (prompt: SavedSunoPrompt) => {
    try {
      const updated = {
        ...prompt,
        favorite: !prompt.favorite,
        updatedAt: new Date().toISOString(),
      }
      await updatePrompt(updated)
      toast.success(updated.favorite ? 'お気に入りに追加しました' : 'お気に入りから削除しました')
    } catch (error) {
      console.error('Favorite toggle error:', error)
      toast.error('更新に失敗しました')
    }
  }, [updatePrompt])

  // 歌詞再生成（単一セクション）
  const handleRegenerateLyricsSection = useCallback((sectionName: string) => {
    setTargetLyricsSection(sectionName)
    setRegenerateError(null)
    setRegenerateDialogOpen(true)
  }, [])

  // 歌詞再生成（複数セクション）
  const handleRegenerateMultipleSections = useCallback((sectionNames: string[]) => {
    if (sectionNames.length === 1) {
      setTargetLyricsSection(sectionNames[0])
    } else {
      setTargetLyricsSection(sectionNames.join(', '))
    }
    setRegenerateError(null)
    setRegenerateDialogOpen(true)
  }, [])

  const handleRegenerateLyrics = useCallback(async (settings: {
    forbiddenWords: string[]
    regenerateReason?: string
    preserveOtherSections: boolean
  }) => {
    if (!selectedPrompt) {
      toast.error('保存されたプロンプトのみ再生成できます')
      return
    }

    try {
      // エラーをクリア
      setRegenerateError(null)

      // 元のプロンプトを保存（まだ保存されていない場合）
      if (!originalPromptBeforeRegenerate) {
        setOriginalPromptBeforeRegenerate(selectedPrompt)
      }

      // targetLyricsSectionがカンマ区切りの場合は配列に変換
      const targetSectionsArray = targetLyricsSection.includes(',')
        ? targetLyricsSection.split(',').map(s => s.trim())
        : [targetLyricsSection]

      const regenerateInput: SunoPromptInput = {
        ...selectedPrompt.input,
        regenerateLyricsSettings: {
          existingPromptResult: selectedPrompt.result,
          targetSections: targetSectionsArray,
          forbiddenWords: settings.forbiddenWords,
          regenerateReason: settings.regenerateReason,
          preserveOtherSections: settings.preserveOtherSections,
        },
      }

      const apiResponse = await generator.mutateAsync(regenerateInput)

      if (!apiResponse || !apiResponse.result) {
        const errorMsg = '再生成に失敗しました。レスポンスが空です。'
        setRegenerateError(errorMsg)
        toast.error(errorMsg)
        return
      }

      const newResult = apiResponse.result as unknown as SunoPromptResult

      // プレビュー状態にする（保存はしない）
      setResult(newResult)
      setIsRegeneratePreview(true)
      setRegenerateDialogOpen(false)
      setRegenerateError(null)
      toast.success('歌詞を再生成しました（プレビュー中）')
    } catch (error) {
      console.error('Regenerate error:', error)

      let errorMsg = '再生成に失敗しました。'

      if (error instanceof Error) {
        if (error.message.includes('JSONパースエラー')) {
          errorMsg = '歌詞の解析に失敗しました。再度お試しください。'
        } else if (error.message.includes('API_KEY')) {
          errorMsg = 'APIキーが無効です。設定を確認してください。'
        } else if (error.message.includes('レート制限')) {
          errorMsg = error.message
        } else {
          errorMsg = `再生成エラー: ${error.message}`
        }
      }

      setRegenerateError(errorMsg)
      toast.error(errorMsg)
    }
  }, [selectedPrompt, targetLyricsSection, generator, originalPromptBeforeRegenerate])

  // 再生成結果を保存
  const handleSaveRegeneratedResult = useCallback(async () => {
    if (!selectedPrompt || !result) {
      toast.error('保存する結果がありません')
      return
    }

    try {
      const updated: SavedSunoPrompt = {
        ...selectedPrompt,
        result: result,
        updatedAt: new Date().toISOString(),
      }

      await updatePrompt(updated)
      setSelectedPrompt(updated)
      setIsRegeneratePreview(false)
      setOriginalPromptBeforeRegenerate(null)
      toast.success('再生成結果を保存しました')
    } catch (error) {
      console.error('Save regenerated result error:', error)
      toast.error('保存に失敗しました')
    }
  }, [selectedPrompt, result, updatePrompt])

  // 元に戻す
  const handleRevertToOriginal = useCallback(() => {
    if (!originalPromptBeforeRegenerate) {
      toast.error('元のデータがありません')
      return
    }

    setResult(originalPromptBeforeRegenerate.result)
    setSelectedPrompt(originalPromptBeforeRegenerate)
    setIsRegeneratePreview(false)
    setOriginalPromptBeforeRegenerate(null)
    toast.success('元の歌詞に戻しました')
  }, [originalPromptBeforeRegenerate])

  // 歌詞を更新
  const handleUpdateLyrics = useCallback(async (newLyrics: string) => {
    if (!selectedPrompt || !result || !result.lyricsWithTags) {
      toast.error('更新できません')
      return
    }

    try {
      // 新しい歌詞からstructureをパース
      const newStructure = parseLyricSections(newLyrics)

      // 新しい結果を作成
      const updatedResult: SunoPromptResult = {
        ...result,
        lyricsWithTags: {
          ...result.lyricsWithTags,
          fullLyrics: newLyrics,
          structure: newStructure,
          totalSections: newStructure.length,
        },
      }

      // 保存済みプロンプトを更新
      const updated: SavedSunoPrompt = {
        ...selectedPrompt,
        result: updatedResult,
        updatedAt: new Date().toISOString(),
      }

      await updatePrompt(updated)
      setResult(updatedResult)
      setSelectedPrompt(updated)
      toast.success('歌詞を保存しました')
    } catch (error) {
      console.error('Update lyrics error:', error)
      toast.error('歌詞の更新に失敗しました')
    }
  }, [selectedPrompt, result, updatePrompt])

  return (
    <SunoErrorBoundary>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Sunoプロンプトジェネレータ
            </h1>
            <p className="text-gray-600 mt-2">
              音楽生成AI「Suno」用の高品質なプロンプトと歌詞を生成します
            </p>
          </div>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">保存済み ({savedPrompts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <Card>
                <SunoPromptForm
                  input={input}
                  onChange={setInput}
                  onGenerate={handleGenerate}
                  isGenerating={generator.isPending}
                  savedWorlds={savedWorlds}
                  characters={characters}
                  lyrics={lyrics}
                  societies={savedSocieties}
                  showBatchSettings={showBatchSettings}
                  onToggleBatchSettings={() => setShowBatchSettings(!showBatchSettings)}
                />
              </Card>

              {(generator.error || generationError) && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {generationError || (generator.error && generator.error.message) || 'エラーが発生しました'}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-6">
              {/* エラー表示（結果エリアにも表示） */}
              {generationError && !generator.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {generationError}
                  </AlertDescription>
                </Alert>
              )}

              {/* バッチ結果表示 */}
              {batchResults.length > 0 && (
                <SunoPromptBatchResults
                  results={batchResults}
                  onSave={handleSave}
                  onSaveAll={handleSaveAll}
                  isSaved={isSaved}
                />
              )}

              {/* 単体結果表示 */}
              {result && batchResults.length === 0 && (
                <ResultDisplay
                  result={result}
                  onSave={() => handleSave()}
                  batchResults={[]}
                  onSelectBatchResult={() => {}}
                  onRegenerateLyricsSection={selectedPrompt ? handleRegenerateLyricsSection : undefined}
                  onRegenerateMultipleSections={selectedPrompt ? handleRegenerateMultipleSections : undefined}
                  isRegeneratePreview={isRegeneratePreview}
                  onSaveRegeneratedResult={isRegeneratePreview ? handleSaveRegeneratedResult : undefined}
                  onRevertToOriginal={isRegeneratePreview ? handleRevertToOriginal : undefined}
                  onUpdateLyrics={selectedPrompt ? handleUpdateLyrics : undefined}
                />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="saved">
          <SavedSunoPromptList
            prompts={savedPrompts}
            onLoad={handleLoadPrompt}
            onDelete={handleDelete}
            onToggleFavorite={handleToggleFavorite}
          />
        </TabsContent>
        </Tabs>
      </div>

      {/* 歌詞再生成ダイアログ */}
      <SunoLyricsRegenerateDialog
        open={regenerateDialogOpen}
        onOpenChange={(open) => {
          setRegenerateDialogOpen(open)
          if (!open) {
            setRegenerateError(null)
          }
        }}
        targetSections={targetLyricsSection ? [targetLyricsSection] : []}
        onRegenerate={handleRegenerateLyrics}
        isGenerating={generator.isPending}
        error={regenerateError}
      />
    </SunoErrorBoundary>
  )
}